import React, { useState, useCallback, useRef } from 'react';
import ReactFlow, {
  addEdge,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  ReactFlowInstance,
  Handle,
  Position,
} from 'reactflow';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faPlay, 
  faCog, 
  faGlobe, 
  faPlus, 
  faSave, 
  faTrash,
  faEdit,
  faCheck,
  faTimes,
  faMessage,
  faCode,
  faServer,
  faComment,
  faFileText,
  faImage,
  faFile,
  faVideo
} from "@fortawesome/free-solid-svg-icons";
import { 
  Box, 
  Typography, 
  Button, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Chip,
  Grid,
  Paper,
  Divider,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { styled } from '@mui/material/styles';
import 'reactflow/dist/style.css';

// Styled components
const FlowContainer = styled(Box)(({ theme }) => ({
  width: '100vw',
  height: '100vh',
  position: 'relative',
  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
}));

const Sidebar = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '300px',
  height: '100%',
  zIndex: 10,
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  borderRight: '1px solid rgba(0, 0, 0, 0.1)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  overflow: 'auto',
}));

const NodePalette = styled(Box)(({ theme }) => ({
  padding: '16px',
  borderBottom: '1px solid #e0e0e0',
}));

const PaletteItem = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  marginBottom: '8px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
}));

const Toolbar = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '16px',
  right: '16px',
  zIndex: 10,
  display: 'flex',
  gap: '8px',
}));

const ToolbarButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
}));

// Custom Node Components
const StartNode = ({ data, isConnectable }) => (
  <div className="bg-green-50 text-green-800 p-4 rounded-lg border-2 border-green-300 min-w-[200px] shadow-sm relative">
    <Handle
      type="source"
      position={Position.Bottom}
      id="output"
      style={{ background: '#10b981', width: '12px', height: '12px' }}
    />
    <div className="flex items-center gap-2 mb-2">
      <FontAwesomeIcon icon={faPlay} className="text-lg text-green-600" />
      <span className="font-semibold">Start Point</span>
    </div>
    <div className="text-sm text-green-700">
      {data.label || 'Message from Client'}
    </div>
    <div className="text-xs mt-2 text-green-600">
      Trigger: {data.trigger || 'New Message'}
    </div>
    {data.messageFilter && (
      <div className="text-xs mt-1 text-green-600">
        Filter: {data.messageFilter}
      </div>
    )}
  </div>
);

const ActionNode = ({ data, isConnectable }) => (
  <div className="bg-blue-50 text-blue-800 p-4 rounded-lg border-2 border-blue-300 min-w-[200px] shadow-sm relative">
    <Handle
      type="target"
      position={Position.Top}
      id="input"
      style={{ background: '#3b82f6', width: '12px', height: '12px' }}
    />
    <Handle
      type="source"
      position={Position.Bottom}
      id="output"
      style={{ background: '#3b82f6', width: '12px', height: '12px' }}
    />
    <div className="flex items-center gap-2 mb-2">
      <FontAwesomeIcon icon={faCog} className="text-lg text-blue-600" />
      <span className="font-semibold">Action</span>
    </div>
    <div className="text-sm text-blue-700">
      {data.label || 'Custom Action'}
    </div>
    <div className="text-xs mt-2 text-blue-600">
      Type: {data.actionType || 'Function Call'}
    </div>
    {data.functionName && (
      <div className="text-xs mt-1 text-blue-600">
        Function: {data.functionName}
      </div>
    )}
  </div>
);

const EndpointNode = ({ data, isConnectable }) => (
  <div className="bg-purple-50 text-purple-800 p-4 rounded-lg border-2 border-purple-300 min-w-[200px] shadow-sm relative">
    <Handle
      type="target"
      position={Position.Top}
      id="input"
      style={{ background: '#8b5cf6', width: '12px', height: '12px' }}
    />
    <Handle
      type="source"
      position={Position.Bottom}
      id="output"
      style={{ background: '#8b5cf6', width: '12px', height: '12px' }}
    />
    <div className="flex items-center gap-2 mb-2">
      <FontAwesomeIcon icon={faGlobe} className="text-lg text-purple-600" />
      <span className="font-semibold">Endpoint</span>
    </div>
    <div className="text-sm text-purple-700">
      {data.label || 'API Call'}
    </div>
    <div className="text-xs mt-2 text-purple-600">
      {data.method || 'GET'} {data.url || 'https://api.example.com'}
    </div>
    {data.headers && (
      <div className="text-xs mt-1 text-purple-600">
        Headers: {Object.keys(data.headers).length} set
      </div>
    )}
  </div>
);

const WhatsAppTextNode = ({ data, isConnectable }) => (
  <div className="bg-orange-50 text-orange-800 p-4 rounded-lg border-2 border-orange-300 min-w-[200px] shadow-sm relative">
    <Handle
      type="target"
      position={Position.Top}
      id="input"
      style={{ background: '#f97316', width: '12px', height: '12px' }}
    />
    <Handle
      type="source"
      position={Position.Bottom}
      id="output"
      style={{ background: '#f97316', width: '12px', height: '12px' }}
    />
    <div className="flex items-center gap-2 mb-2">
      <FontAwesomeIcon icon={faComment} className="text-lg text-orange-600" />
      <span className="font-semibold">WhatsApp Text</span>
    </div>
    <div className="text-sm text-orange-700">
      {data.label || 'Text Response'}
    </div>
    <div className="text-xs mt-2 text-orange-600">
      Message: {data.message ? data.message.substring(0, 30) + '...' : 'No message set'}
    </div>
    {data.waitForResponse && (
      <div className="text-xs mt-1 text-orange-600">
        Wait for response: Yes
      </div>
    )}
  </div>
);

const WhatsAppTemplateNode = ({ data, isConnectable }) => (
  <div className="bg-indigo-50 text-indigo-800 p-4 rounded-lg border-2 border-indigo-300 min-w-[200px] shadow-sm relative">
    <Handle
      type="target"
      position={Position.Top}
      id="input"
      style={{ background: '#6366f1', width: '12px', height: '12px' }}
    />
    <Handle
      type="source"
      position={Position.Bottom}
      id="output"
      style={{ background: '#6366f1', width: '12px', height: '12px' }}
    />
    <div className="flex items-center gap-2 mb-2">
      <FontAwesomeIcon icon={faFileText} className="text-lg text-indigo-600" />
      <span className="font-semibold">WhatsApp Template</span>
    </div>
    <div className="text-sm text-indigo-700">
      {data.label || 'Template Response'}
    </div>
    <div className="text-xs mt-2 text-indigo-600">
      Template: {data.templateName || 'No template'}
    </div>
    {data.mediaType && (
      <div className="flex items-center gap-1 mt-1">
        <FontAwesomeIcon 
          icon={data.mediaType === 'image' ? faImage : 
                data.mediaType === 'document' ? faFile : 
                data.mediaType === 'video' ? faVideo : faFile} 
          className="text-xs text-indigo-500" 
        />
        <span className="text-xs text-indigo-500">{data.mediaType}</span>
      </div>
    )}
    {data.waitForResponse && (
      <div className="text-xs mt-1 text-indigo-600">
        Wait for response: Yes
      </div>
    )}
  </div>
);

const nodeTypes = {
  start: StartNode,
  action: ActionNode,
  endpoint: EndpointNode,
  whatsapp_text: WhatsAppTextNode,
  whatsapp_template: WhatsAppTemplateNode,
};

const initialNodes = [
  {
    id: '1',
    type: 'start',
    position: { x: 250, y: 100 },
    data: { label: 'Message from Client', trigger: 'New Message' },
  },
];

const initialEdges = [];

export default function Flujos() {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState(null);
  const [configDialog, setConfigDialog] = useState(false);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [nodeCounter, setNodeCounter] = useState(2);

  const onConnect = useCallback((params) => setEdges((eds) => addEdge(params, eds)), []);

  const addNode = (type) => {
    const newNode = {
      id: nodeCounter.toString(),
      type: type,
      position: { x: Math.random() * 400 + 100, y: Math.random() * 300 + 100 },
      data: { 
        label: type === 'start' ? 'Message from Client' : 
               type === 'action' ? 'Custom Action' : 
               type === 'endpoint' ? 'API Call' :
               type === 'whatsapp_text' ? 'WhatsApp Text Response' :
               type === 'whatsapp_template' ? 'WhatsApp Template Response' : 'Node',
        trigger: type === 'start' ? 'New Message' : undefined,
        messageFilter: type === 'start' ? undefined : undefined,
        actionType: type === 'action' ? 'Function Call' : undefined,
        functionName: type === 'action' ? undefined : undefined,
        method: type === 'endpoint' ? 'GET' : undefined,
        url: type === 'endpoint' ? 'https://api.example.com' : undefined,
        headers: type === 'endpoint' ? {} : undefined,
        body: type === 'endpoint' ? undefined : undefined,
        message: type === 'whatsapp_text' ? 'Hello! How can I help you?' : undefined,
        waitForResponse: type === 'whatsapp_text' ? false : undefined,
        templateName: type === 'whatsapp_template' ? 'welcome_template' : undefined,
        mediaType: type === 'whatsapp_template' ? undefined : undefined,
        textParameters: type === 'whatsapp_template' ? [] : undefined,
        buttonParameters: type === 'whatsapp_template' ? [] : undefined,
        waitForResponse: type === 'whatsapp_template' ? false : undefined,
      },
    };
    setNodes((nds) => [...nds, newNode]);
    setNodeCounter(nodeCounter + 1);
  };

  const onNodeClick = (event, node) => {
    setSelectedNode(node);
    setConfigDialog(true);
  };

  const updateNode = (nodeId, newData) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId ? { ...node, data: { ...node.data, ...newData } } : node
      )
    );
    setConfigDialog(false);
    setSelectedNode(null);
  };

  const deleteNode = (nodeId) => {
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
    setConfigDialog(false);
    setSelectedNode(null);
  };

  const saveWorkflow = () => {
    const workflow = {
      nodes: nodes,
      edges: edges,
      timestamp: new Date().toISOString(),
    };
    console.log('Saving workflow:', workflow);
    // Here you would typically save to your backend
  };

  return (
    <ReactFlowProvider>
      <FlowContainer>
        <Sidebar>
          <NodePalette>
            <Typography variant="h6" className="mb-4 font-semibold text-gray-700">
              Node Palette
            </Typography>
            
            <PaletteItem onClick={() => addNode('start')}>
              <CardContent className="p-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faMessage} className="text-green-600" />
                  </div>
                  <div>
                    <Typography variant="subtitle2" className="font-semibold">
                      Start Point
                    </Typography>
                    <Typography variant="caption" className="text-gray-500">
                      Message trigger
                    </Typography>
                  </div>
                </div>
              </CardContent>
            </PaletteItem>

            <PaletteItem onClick={() => addNode('action')}>
              <CardContent className="p-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faCode} className="text-blue-600" />
                  </div>
                  <div>
                    <Typography variant="subtitle2" className="font-semibold">
                      Action
                    </Typography>
                    <Typography variant="caption" className="text-gray-500">
                      Function call
                    </Typography>
                  </div>
                </div>
              </CardContent>
            </PaletteItem>

            <PaletteItem onClick={() => addNode('endpoint')}>
              <CardContent className="p-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faServer} className="text-purple-600" />
                  </div>
                  <div>
                    <Typography variant="subtitle2" className="font-semibold">
                      Endpoint
                    </Typography>
                    <Typography variant="caption" className="text-gray-500">
                      API call
                    </Typography>
                  </div>
                </div>
              </CardContent>
            </PaletteItem>

            <PaletteItem onClick={() => addNode('whatsapp_text')}>
              <CardContent className="p-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faComment} className="text-orange-600" />
                  </div>
                  <div>
                    <Typography variant="subtitle2" className="font-semibold">
                      WhatsApp Text
                    </Typography>
                    <Typography variant="caption" className="text-gray-500">
                      Simple text response
                    </Typography>
                  </div>
                </div>
              </CardContent>
            </PaletteItem>

            <PaletteItem onClick={() => addNode('whatsapp_template')}>
              <CardContent className="p-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faFileText} className="text-indigo-600" />
                  </div>
                  <div>
                    <Typography variant="subtitle2" className="font-semibold">
                      WhatsApp Template
                    </Typography>
                    <Typography variant="caption" className="text-gray-500">
                      Template with parameters
                    </Typography>
                  </div>
                </div>
              </CardContent>
            </PaletteItem>
          </NodePalette>

          <Box className="p-4">
            <Typography variant="h6" className="mb-4 font-semibold text-gray-700">
              Workflow Info
            </Typography>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Nodes:</span>
                <Chip label={nodes.length} size="small" color="primary" />
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Connections:</span>
                <Chip label={edges.length} size="small" color="secondary" />
              </div>
            </div>
          </Box>
        </Sidebar>

        <Toolbar>
          <ToolbarButton
            variant="contained"
            color="primary"
            startIcon={<FontAwesomeIcon icon={faSave} />}
            onClick={saveWorkflow}
          >
            Save Workflow
          </ToolbarButton>
        </Toolbar>

        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onInit={setReactFlowInstance}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          fitView
          attributionPosition="bottom-left"
        >
          <MiniMap
            style={{
              height: 120,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
            }}
            zoomable
            pannable
          />
          <Controls />
          <Background color="#aaa" gap={16} />
        </ReactFlow>

        {/* Node Configuration Dialog */}
        <Dialog
          open={configDialog}
          onClose={() => setConfigDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle className="bg-gradient-to-r from-blue-500 to-purple-600 text-white">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faEdit} />
              Configure Node
            </div>
          </DialogTitle>
          <DialogContent className="p-6">
            {selectedNode && (
              <NodeConfigForm
                node={selectedNode}
                onUpdate={updateNode}
                onDelete={deleteNode}
              />
            )}
          </DialogContent>
        </Dialog>
      </FlowContainer>
    </ReactFlowProvider>
  );
}

// Node Configuration Form Component
function NodeConfigForm({ node, onUpdate, onDelete }) {
  const [formData, setFormData] = useState({
    label: node.data.label || '',
    trigger: node.data.trigger || '',
    messageFilter: node.data.messageFilter || '',
    actionType: node.data.actionType || '',
    functionName: node.data.functionName || '',
    method: node.data.method || 'GET',
    url: node.data.url || '',
    headers: node.data.headers || {},
    body: node.data.body || '',
    message: node.data.message || '',
    waitForResponse: node.data.waitForResponse || false,
    templateName: node.data.templateName || '',
    mediaType: node.data.mediaType || '',
    textParameters: node.data.textParameters || [],
    buttonParameters: node.data.buttonParameters || [],
    description: node.data.description || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(node.id, formData);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this node?')) {
      onDelete(node.id);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Node Label"
            value={formData.label}
            onChange={(e) => setFormData({ ...formData, label: e.target.value })}
            variant="outlined"
            className="rounded-lg"
          />
        </Grid>

        {node.type === 'start' && (
          <>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Trigger Type</InputLabel>
                <Select
                  value={formData.trigger}
                  onChange={(e) => setFormData({ ...formData, trigger: e.target.value })}
                  label="Trigger Type"
                >
                  <MenuItem value="New Message">New Message</MenuItem>
                  <MenuItem value="Keyword Match">Keyword Match</MenuItem>
                  <MenuItem value="Time Based">Time Based</MenuItem>
                  <MenuItem value="User Action">User Action</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Message Filter"
                value={formData.messageFilter}
                onChange={(e) => setFormData({ ...formData, messageFilter: e.target.value })}
                variant="outlined"
                placeholder="Filter messages containing specific text..."
              />
            </Grid>
          </>
        )}

        {node.type === 'action' && (
          <>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Action Type</InputLabel>
                <Select
                  value={formData.actionType}
                  onChange={(e) => setFormData({ ...formData, actionType: e.target.value })}
                  label="Action Type"
                >
                  <MenuItem value="Function Call">Function Call</MenuItem>
                  <MenuItem value="Data Processing">Data Processing</MenuItem>
                  <MenuItem value="Conditional Logic">Conditional Logic</MenuItem>
                  <MenuItem value="Variable Assignment">Variable Assignment</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Function Name"
                value={formData.functionName}
                onChange={(e) => setFormData({ ...formData, functionName: e.target.value })}
                variant="outlined"
                placeholder="myCustomFunction"
              />
            </Grid>
          </>
        )}

        {node.type === 'endpoint' && (
          <>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>HTTP Method</InputLabel>
                <Select
                  value={formData.method}
                  onChange={(e) => setFormData({ ...formData, method: e.target.value })}
                  label="HTTP Method"
                >
                  <MenuItem value="GET">GET</MenuItem>
                  <MenuItem value="POST">POST</MenuItem>
                  <MenuItem value="PUT">PUT</MenuItem>
                  <MenuItem value="PATCH">PATCH</MenuItem>
                  <MenuItem value="DELETE">DELETE</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="API Endpoint URL"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                variant="outlined"
                placeholder="https://api.example.com/endpoint"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Headers (JSON format)"
                value={JSON.stringify(formData.headers, null, 2)}
                onChange={(e) => {
                  try {
                    const headers = JSON.parse(e.target.value);
                    setFormData({ ...formData, headers });
                  } catch (error) {
                    // Invalid JSON, keep the text for user to fix
                  }
                }}
                variant="outlined"
                multiline
                rows={3}
                placeholder='{"Content-Type": "application/json", "Authorization": "Bearer token"}'
              />
            </Grid>
            {(formData.method === 'POST' || formData.method === 'PUT' || formData.method === 'PATCH') && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Request Body (JSON format)"
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  variant="outlined"
                  multiline
                  rows={4}
                  placeholder='{"key": "value", "data": "example"}'
                />
              </Grid>
            )}
          </>
        )}

        {node.type === 'whatsapp_text' && (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Message Text"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                variant="outlined"
                multiline
                rows={4}
                placeholder="Enter the text message to send..."
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.waitForResponse}
                    onChange={(e) => setFormData({ ...formData, waitForResponse: e.target.checked })}
                  />
                }
                label="Wait for user response"
              />
            </Grid>
          </>
        )}

        {node.type === 'whatsapp_template' && (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Template Name"
                value={formData.templateName}
                onChange={(e) => setFormData({ ...formData, templateName: e.target.value })}
                variant="outlined"
                placeholder="welcome_template"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Media Type</InputLabel>
                <Select
                  value={formData.mediaType}
                  onChange={(e) => setFormData({ ...formData, mediaType: e.target.value })}
                  label="Media Type"
                >
                  <MenuItem value="">No Media</MenuItem>
                  <MenuItem value="image">Image</MenuItem>
                  <MenuItem value="document">Document</MenuItem>
                  <MenuItem value="video">Video</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" className="mb-2">
                Text Parameters
              </Typography>
              <TextField
                fullWidth
                label="Text Parameters (comma-separated)"
                value={formData.textParameters.join(', ')}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  textParameters: e.target.value.split(',').map(p => p.trim()).filter(p => p)
                })}
                variant="outlined"
                placeholder="param1, param2, param3"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" className="mb-2">
                Button Parameters
              </Typography>
              <TextField
                fullWidth
                label="Button Parameters (comma-separated)"
                value={formData.buttonParameters.join(', ')}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  buttonParameters: e.target.value.split(',').map(p => p.trim()).filter(p => p)
                })}
                variant="outlined"
                placeholder="button1, button2, button3"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.waitForResponse}
                    onChange={(e) => setFormData({ ...formData, waitForResponse: e.target.checked })}
                  />
                }
                label="Wait for user response"
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            variant="outlined"
            multiline
            rows={3}
            placeholder="Describe what this node does..."
          />
        </Grid>
      </Grid>

      <Divider />

      <DialogActions className="p-0">
        <Button
          onClick={handleDelete}
          color="error"
          startIcon={<FontAwesomeIcon icon={faTrash} />}
        >
          Delete Node
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        <Button
          onClick={() => onUpdate(node.id, {})}
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          startIcon={<FontAwesomeIcon icon={faCheck} />}
        >
          Save Changes
        </Button>
      </DialogActions>
    </form>
  );
}
